/** Subscribes the TanStack issue viewer data to Alive. */
import {debounce} from '@github/mini-throttle'
import {useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import {useCallback, useMemo} from 'react'

import {invalidateScopes, type InvalidationScope} from './secondary-view-invalidation'

const BATCH_MS = 250

/** `wait` is the server's replication lag: refetching sooner risks reading a stale replica. */
type AliveNotification = {
  wait?: number
  timestamp?: number
  reason?: string
  gid?: string
  issue_state_updated?: true
  issue_title_updated?: true
  issue_body_updated?: true
  issue_transfer_state_updated?: true
  issue_timeline_updated?: true
  parent_issue_updated?: true
  issue_dependencies_summary_updated?: true
  update_intent_changed?: true
}

function runAfterAliveWait(run: () => void, data: AliveNotification | undefined): void {
  window.setTimeout(run, data?.wait ?? 0)
}

const ISSUE_CHANNEL_SCOPE_BY_FIELD = {
  issue_state_updated: 'header',
  issue_transfer_state_updated: 'header',
  parent_issue_updated: 'metadata',
  issue_dependencies_summary_updated: 'metadata',
} as const satisfies Partial<Record<keyof AliveNotification, InvalidationScope>>

function scopesForNotification(data: AliveNotification): readonly InvalidationScope[] {
  const scopes = new Set<InvalidationScope>()
  for (const [field, scope] of Object.entries(ISSUE_CHANNEL_SCOPE_BY_FIELD) as Array<
    [keyof typeof ISSUE_CHANNEL_SCOPE_BY_FIELD, InvalidationScope]
  >) {
    if (data[field] != null) scopes.add(scope)
  }
  return [...scopes]
}

type LiveUpdateProps = {
  channel: string | null | undefined
  owner: string
  repo: string
  number: number
  issueId: string | undefined
  /** Set by callers whose header and non-metadata fields render from a page payload. */
  routeQueryKey?: readonly unknown[]
}

export function SecondaryViewLiveUpdates({
  channel,
  owner,
  repo,
  number,
  issueId,
  routeQueryKey,
}: LiveUpdateProps): React.JSX.Element | null {
  const queryClient = useQueryClient()
  const isMounted = useIsMounted()

  // Primitives rather than one object prop: a fresh literal each render would rebuild the throttles
  // and make `useAlive` resubscribe every render, dropping events in the gap.
  const coordinates = useMemo(() => ({owner, repo, number}), [owner, repo, number])

  const invalidateFor = useCallback(
    (scopes: readonly InvalidationScope[]) => {
      if (!isMounted() || !issueId) return
      invalidateScopes({queryClient, scopes, issueId, coordinates, routeQueryKey})
    },
    [isMounted, issueId, queryClient, coordinates, routeQueryKey],
  )

  const queueNotification = useMemo(() => {
    const queuedScopes = new Set<InvalidationScope>()
    const flush = debounce(() => {
      const scopes = [...queuedScopes]
      queuedScopes.clear()
      invalidateFor(scopes)
    }, BATCH_MS)

    return (data: AliveNotification, scopes: readonly InvalidationScope[]) => {
      if (scopes.length === 0) return
      runAfterAliveWait(() => {
        for (const scope of scopes) queuedScopes.add(scope)
        flush()
      }, data)
    }
  }, [invalidateFor])

  const handleIssueUpdate = useCallback(
    (data: AliveNotification) => {
      queueNotification(data, scopesForNotification(data))
    },
    [queueNotification],
  )

  useAlive(channel ?? null, handleIssueUpdate)
  return null
}
